// Firmwares — uploaded firmware files for IP phones, with bulk Upgrade.
// Mirrors 3CX 144420 toolbar: + Add | Upgrade | Search

import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, ArrowUpCircle, Search, Trash2 } from 'lucide-react';
import { getToken } from '../lib/api';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

export default function PhoneFirmwares() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const { data: rows = [] } = useQuery<any[]>({
    queryKey: ['phone-firmwares'],
    queryFn: () => fJson('/api/phones/firmwares').catch(() => []),
    refetchInterval: 30_000,
  });

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return (rows as any[]).filter((r: any) =>
      [r.file, r.version, r.vendor, r.model].filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [rows, search]);

  function togglePick(id: string) {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  }
  function togglePickAll() {
    if (picked.size > 0) setPicked(new Set());
    else setPicked(new Set(filtered.map((r: any) => r.firmware_id)));
  }

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch('/api/phones/firmwares', { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['phone-firmwares'] }),
    onError: (e: any) => alert(`Upload failed: ${e.message}`),
  });

  const upgrade = useMutation({
    mutationFn: () => fJson('/api/phones/firmwares/upgrade', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ firmware_ids: Array.from(picked) }),
    }),
    onSuccess: (r: any) => alert(`Upgrade enqueued for ${r.count || picked.size} phone(s).`),
    onError: (e: any) => alert(`Upgrade failed: ${e.message}`),
  });

  const remove = useMutation({
    mutationFn: (id: string) => fJson(`/api/phones/firmwares/${id}`, { method: 'DELETE' }),
    onSuccess: () => { setPicked(new Set()); qc.invalidateQueries({ queryKey: ['phone-firmwares'] }); },
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/phones')} className="btn-ghost inline-flex items-center gap-1"><ArrowLeft size={14} /> Phones</button>
        <h1 className="text-xl font-semibold">Firmwares</h1>
      </div>

      <div className="card p-2 flex flex-wrap gap-1 items-center">
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => fileInput.current?.click()} disabled={upload.isPending}>
          <Plus size={14} /> {upload.isPending ? 'Uploading…' : 'Add'}
        </button>
        <button className="btn-ghost text-sm inline-flex items-center gap-1 disabled:opacity-40"
                onClick={() => picked.size && confirm(`Push the selected firmware to all matching phones?`) && upgrade.mutate()}
                disabled={picked.size === 0 || upgrade.isPending}>
          <ArrowUpCircle size={14} /> Upgrade
        </button>
        <input ref={fileInput} type="file" className="hidden" accept=".bin,.fw,.rom,.zip"
               onChange={e => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ''; }} />

        <div className="flex-1" />

        <div className="flex items-center gap-1 bg-elevated rounded-md px-2 py-1">
          <Search size={14} className="text-textSecondary" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
                 className="bg-transparent outline-none text-sm w-48" />
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th className="w-8">
                <input type="checkbox" checked={picked.size > 0 && picked.size === filtered.length} onChange={togglePickAll} />
              </th>
              <th>File</th>
              <th>Version</th>
              <th>Vendor</th>
              <th>Model</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r: any) => (
              <tr key={r.firmware_id} className="hover:bg-elevated">
                <td><input type="checkbox" checked={picked.has(r.firmware_id)} onChange={() => togglePick(r.firmware_id)} /></td>
                <td className="font-mono text-xs">{r.file || r.filename || '—'}</td>
                <td className="font-mono text-xs">{r.version || '—'}</td>
                <td>{r.vendor || '—'}</td>
                <td>{r.model || '—'}</td>
                <td>
                  <button className="btn-ghost p-1 text-danger" title="Delete"
                          onClick={() => confirm('Delete firmware file?') && remove.mutate(r.firmware_id)}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-textSecondary">
                No firmware files uploaded. Click <strong>Add</strong> to upload a .bin / .fw / .rom file.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
